/**
 * Traffic histograms — what the ad server expects to see over the coming week,
 * broken down by one of its traffic dimensions.
 *
 * The endpoint scopes to one site or ad unit at a time (`site` / `adUnit`), and
 * narrows what it counts with `filter`, whose keys are traffic types in their
 * own right — so countries are a filter, while sites are not.
 */
import { apiFetch } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** The dimensions the histogram can report on, as the API names them. */
export type TrafficType =
  | 'SEGMENT'
  | 'CATEGORY'
  | 'KEYWORD'
  | 'KEY_VALUES'
  | 'COUNTRY'
  | 'REGION'
  | 'CITY'
  | 'POSTCODE'
  | 'DEVICE'
  | 'CREATIVE_SIZE'
  | 'CREATIVE_TYPE'

/** The object an entry stands for, when the value names one. */
export interface HistogramObject {
  id: string
  name: string
  descriptions?: string
}

export interface HistogramEntry {
  /** The dimension's value, e.g. `300x250` for a creative size. */
  value: string
  /** Its presence in the traffic analysed, as a percentage, 0–100. */
  frequency: number
  /** Requests this value is expected to see over the coming week. */
  expectedNextWeek: number
  /**
   * Entries standing for an object carry it under its type name in lower camel
   * case — `userSegment` for a segment — with the id and name resolved.
   */
  [object: string]: unknown
}

export interface TrafficHistogramRequest {
  types: TrafficType[]
  /**
   * Values to narrow the traffic by, keyed by the type they belong to. These
   * are the values as the histogram reports them, so `COUNTRY` takes a
   * country's full name — "Norway", not "NO".
   */
  filter?: Partial<Record<TrafficType, string[]>>
  /** One site to report on. The endpoint takes a single site, not a list. */
  site?: string
  adUnit?: string
  startDate?: string
  endDate?: string
  suppressAdUnitBreakdown?: boolean
}

export type TrafficHistogramResponse = Partial<
  Record<TrafficType, { histogram: HistogramEntry[] }>
>

export const TrafficHistogramApi = {
  get: async (request: TrafficHistogramRequest): Promise<TrafficHistogramResponse> => {
    const res = await apiFetch(`${API_BASE}/traffichistogram?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ suppressAdUnitBreakdown: true, ...request }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch traffic histogram')
    return res.json()
  },
}
